<template>
  <div class="pagination">
    <span
      class="previous"
      :class="{ disabled: selectedPage === 1 }"
      @click="handlePage('previous')"
    >
      <BaseSvg :src="ChevronLeft" alt="left-black" />
      {{ !hideNavigationText ? paginationTexts?.[0] : "" }}
    </span>
    <div class="paginate-container">
      <template
        v-for="(pageValue, index) in calculatePaginationDesign"
        :key="index"
      >
        <template v-if="pageValue === 'firstHiddenPages'">
          <HiddenPages
            :position="props.hidePagePosition"
            :pages="firstHiddenPages"
            @on-click="handlePage"
          />
        </template>
        <template v-else-if="pageValue === 'secondHiddenPages'">
          <HiddenPages
            :position="props.hidePagePosition"
            :pages="secondHiddenPages"
            @on-click="handlePage"
          />
        </template>
        <span
          v-else
          class="page"
          :class="{ selected: pageValue === selectedPage }"
          @click="handlePage(pageValue)"
        >
          {{ pageValue }}
        </span>
      </template>
    </div>
    <span
      class="next"
      :class="{ disabled: selectedPage === totalPages || !totalPages }"
      @click="handlePage('next')"
    >
      {{ !hideNavigationText ? paginationTexts?.[1] : "" }}
      <BaseSvg :src="ChevronRight" alt="left-black" />
    </span>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, watchEffect, expose } from "vue";
import { ChevronLeft, ChevronRight } from "@/assets/icons";
import HiddenPages from "@/components/Base/subcomponents/BasePagination/HiddenPages.vue";
import BaseSvg from "./BaseSvg.vue";

export interface IProps {
  paginationTexts?: string[];
  totalCount: number;
  page: number;
  siblingCount?: number;
  perPage?: number;
  hidePagePosition?: "top" | "bottom";
  hideNavigationText?: boolean;
  totalPages: number;
}

const props = withDefaults(defineProps<IProps>(), {
  paginationTexts: () => ["Önceki", "Sonraki"],
  page: 1,
  siblingCount: 2,
  perPage: 10,
  hidePagePosition: "top",
  hideNavigationText: false,
});

const emit = defineEmits(["change"]);
const selectedPage = ref<any>(props.page);
// Sayfa dışarıdan değişirse (ör. arama sonrası 1. sayfaya dönüş) seçili sayfa da güncellenir.
watch(
  () => props.page,
  (page) => {
    selectedPage.value = page;
  }
);
const firstHiddenPages = ref<number[]>([]);
const calculatePaginationDesign = ref<(string | number)[]>([]);
const secondHiddenPages = ref<number[]>([]);

function calcArrayNumber(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}
watchEffect(() => {
  const showFirstThreeDot = 1 + props.siblingCount * 2;
  const showLastThreeDot = props.totalPages - props.siblingCount * 2;
  if (props.totalPages > showFirstThreeDot + 2) {
    if (selectedPage.value < showFirstThreeDot) {
      secondHiddenPages.value = calcArrayNumber(
        showFirstThreeDot + 1,
        props.totalPages - 1
      );
      firstHiddenPages.value = [];
      calculatePaginationDesign.value = [
        1,
        ...calcArrayNumber(2, showFirstThreeDot),
        "secondHiddenPages",
        props.totalPages,
      ];
      return;
    } else if (
      selectedPage.value >= showFirstThreeDot &&
      selectedPage.value <= showLastThreeDot
    ) {
      firstHiddenPages.value = calcArrayNumber(
        2,
        selectedPage.value - (props.siblingCount + 1)
      );
      secondHiddenPages.value = calcArrayNumber(
        selectedPage.value + (props.siblingCount + 1),
        props.totalPages - 1
      );
      calculatePaginationDesign.value = [
        1,
        "firstHiddenPages",
        ...calcArrayNumber(
          selectedPage.value - props.siblingCount,
          selectedPage.value + props.siblingCount
        ),
        "secondHiddenPages",
        props.totalPages,
      ];
      return;
    } else if (selectedPage.value > showLastThreeDot) {
      firstHiddenPages.value = calcArrayNumber(2, showLastThreeDot - 1);
      secondHiddenPages.value = [];
      calculatePaginationDesign.value = [
        1,
        "firstHiddenPages",
        ...calcArrayNumber(showLastThreeDot, props.totalPages - 1),
        props.totalPages,
      ];
      return;
    }
  } else {
    calculatePaginationDesign.value = [...calcArrayNumber(1, props.totalPages)];
  }
});

const handlePage = (page: any): void => {
  if (typeof page === "number" && selectedPage.value !== page) {
    selectedPage.value = page;
  } else if (
    page === "next" &&
    selectedPage.value !== props.totalPages &&
    !!props.totalPages
  ) {
    selectedPage.value += 1;
  } else if (page === "previous" && selectedPage.value !== 1) {
    selectedPage.value -= 1;
  } else {
    return;
  }
  emit("change", selectedPage.value);
};

defineExpose({ handlePage });
</script>

<style lang="scss" scoped src="./styles/pagination.scss" />
